// Step de Endereço do onboarding
import { useState } from 'react';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { Button } from '../common/Button';
import { Loader2 } from 'lucide-react';
import { formatCEP } from '../../utils/formatters';

const ESTADOS = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

interface AddressStepProps {
  data: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    estado: string;
  };
  onChange: (data: Partial<AddressStepProps['data']>) => void;
  onNext: () => void;
  onBack: () => void;
}

export function AddressStep({ data, onChange, onNext, onBack }: AddressStepProps) {
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const buscarCEP = async (cep: string) => {
    const cleaned = cep.replace(/\D/g, '');
    if (cleaned.length !== 8) return;

    setLoadingCEP(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleaned}/json/`);
      const result = await response.json();

      if (!result.erro) {
        onChange({
          logradouro: result.logradouro || '',
          bairro: result.bairro || '',
          cidade: result.localidade || '',
          estado: result.uf || '',
        });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoadingCEP(false);
    }
  };

  const handleCEPChange = (value: string) => {
    const formatted = formatCEP(value);
    onChange({ cep: formatted });
    
    // Buscar CEP automaticamente quando tiver 8 dígitos
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 8) {
      buscarCEP(cleaned);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!data.cep.trim()) {
      newErrors.cep = 'CEP é obrigatório';
    } else {
      const cleaned = data.cep.replace(/\D/g, '');
      if (cleaned.length !== 8) {
        newErrors.cep = 'CEP inválido';
      }
    }

    if (!data.logradouro.trim()) {
      newErrors.logradouro = 'Logradouro é obrigatório';
    }

    if (!data.numero.trim()) {
      newErrors.numero = 'Número é obrigatório';
    }

    if (!data.bairro.trim()) {
      newErrors.bairro = 'Bairro é obrigatório';
    }

    if (!data.cidade.trim()) {
      newErrors.cidade = 'Cidade é obrigatória';
    }

    if (!data.estado) {
      newErrors.estado = 'Estado é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Endereço da Empresa
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Preencha o endereço completo da sua empresa
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
            <div className="relative">
              <Input
                label="CEP"
                value={data.cep}
                onChange={(e) => handleCEPChange(e.target.value)}
                error={errors.cep}
                required
                placeholder="00000-000"
                maxLength={9}
                disabled={loadingCEP}
                className={loadingCEP ? 'pr-10' : ''}
              />
              {loadingCEP && (
                <div className="absolute right-3 top-9">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
        </div>

        <div className="md:col-span-2">
          <Input
            label="Logradouro"
            value={data.logradouro}
            onChange={(e) => onChange({ logradouro: e.target.value })}
            error={errors.logradouro}
            required
            placeholder="Rua, Avenida, etc."
          />
        </div>

        <Input
          label="Número"
          value={data.numero}
          onChange={(e) => onChange({ numero: e.target.value })}
          error={errors.numero}
          required
          placeholder="123"
        />

        <div className="md:col-span-2">
          <Input
            label="Complemento"
            value={data.complemento}
            onChange={(e) => onChange({ complemento: e.target.value })}
            placeholder="Sala, Andar, etc."
          />
        </div>

        <Input
          label="Bairro"
          value={data.bairro}
          onChange={(e) => onChange({ bairro: e.target.value })}
          error={errors.bairro}
          required
          placeholder="Nome do bairro"
        />

        <Input
          label="Cidade"
          value={data.cidade}
          onChange={(e) => onChange({ cidade: e.target.value })}
          error={errors.cidade}
          required
          placeholder="Nome da cidade"
        />

        <Select
          label="Estado"
          value={data.estado}
          onChange={(e) => onChange({ estado: e.target.value })}
          error={errors.estado}
          required
          options={ESTADOS}
        />
      </div>

      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="secondary" onClick={onBack}>
          Voltar
        </Button>
        <Button variant="primary" onClick={handleNext}>
          Próximo
        </Button>
      </div>
    </div>
  );
}
